import { useDeferredValue, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { localizedName, type AppLang } from '@/lib/format'
import { getT } from '@/lib/i18n'
import type { NpCityOut, NpWarehouseOut } from '@/shared/types'

interface Props {
  lang: AppLang
  city: string
  cityRef: string
  warehouse: string
  warehouseRef: string
  onCityChange: (city: string, ref: string) => void
  onWarehouseChange: (warehouse: string, ref: string) => void
}

export function NovaPoshtaFields({
  lang,
  city,
  cityRef,
  warehouse,
  warehouseRef,
  onCityChange,
  onWarehouseChange,
}: Props) {
  const T = getT(lang, 'novaPoshta')
  const deferredCity = useDeferredValue(city.trim())
  const deferredWarehouse = useDeferredValue(warehouse.trim())

  const citiesQuery = useQuery<NpCityOut[]>({
    queryKey: ['np-cities', deferredCity],
    queryFn: () => api.get<NpCityOut[]>(`/delivery/np/cities?q=${encodeURIComponent(deferredCity)}&limit=7`),
    enabled: deferredCity.length >= 2 && !cityRef,
    retry: false,
  })

  const warehousesQuery = useQuery<NpWarehouseOut[]>({
    queryKey: ['np-warehouses', cityRef, deferredWarehouse],
    queryFn: () =>
      api.get<NpWarehouseOut[]>(
        `/delivery/np/warehouses?city_ref=${encodeURIComponent(cityRef)}&q=${encodeURIComponent(deferredWarehouse)}&limit=${warehouseRef ? 200 : 50}`,
      ),
    enabled: Boolean(cityRef),
    retry: false,
  })

  const lookupUnavailable = citiesQuery.error instanceof Error && citiesQuery.error.message === 'nova_poshta_not_configured'
  const cityOptions = useMemo(() => citiesQuery.data ?? [], [citiesQuery.data])
  const warehouseOptions = useMemo(() => warehousesQuery.data ?? [], [warehousesQuery.data])

  useEffect(() => {
    if (cityRef || city.trim().length < 2 || cityOptions.length === 0) return
    const normalizedCity = city.trim().toLowerCase()
    const exactMatch = cityOptions.find(
      (option) => localizedName(lang, option.name_uk).trim().toLowerCase() === normalizedCity,
    )
    if (exactMatch) {
      onCityChange(localizedName(lang, exactMatch.name_uk), exactMatch.ref)
    }
  }, [city, cityOptions, cityRef, lang, onCityChange])

  useEffect(() => {
    if (!cityRef || warehouseRef || warehouse.trim().length < 1 || warehouseOptions.length === 0) return
    const normalizedWarehouse = warehouse.trim().toLowerCase()
    const exactMatch = warehouseOptions.find(
      (option) => option.description_uk.trim().toLowerCase() === normalizedWarehouse,
    )
    if (exactMatch) {
      onWarehouseChange(exactMatch.description_uk, exactMatch.ref)
    }
  }, [cityRef, onWarehouseChange, warehouse, warehouseOptions, warehouseRef])

  useEffect(() => {
    if (!cityRef || !warehouseRef || warehouse.trim() || warehouseOptions.length === 0) return
    const exactMatch = warehouseOptions.find((option) => option.ref === warehouseRef)
    if (exactMatch) {
      onWarehouseChange(exactMatch.description_uk, exactMatch.ref)
    }
  }, [cityRef, onWarehouseChange, warehouse, warehouseOptions, warehouseRef])

  return (
    <div className="space-y-4">
      <div>
        {cityRef ? (
          <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
            <p className="text-xs font-medium text-brand-700">{T.selectedCity}</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{city}</p>
            <button
              type="button"
              onClick={() => onCityChange('', '')}
              className="mt-2 text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              {T.changeCity}
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={city}
              onChange={(e) => onCityChange(e.target.value, '')}
              placeholder={T.cityPlaceholder}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              {lookupUnavailable ? T.notConfigured : T.cityHint}
            </p>
            {!lookupUnavailable && !cityRef && deferredCity.length >= 2 && (
              <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white">
                {citiesQuery.isLoading && (
                  <p className="px-4 py-3 text-sm text-gray-500">{T.loading}</p>
                )}
                {!citiesQuery.isLoading && cityOptions.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-500">{T.noCities}</p>
                )}
                {cityOptions.map((option) => (
                  <button
                    key={option.ref}
                    type="button"
                    onClick={() => onCityChange(localizedName(lang, option.name_uk), option.ref)}
                    className="block w-full border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50"
                  >
                    <span className="block font-medium">{localizedName(lang, option.name_uk)}</span>
                    <span className="block text-xs text-gray-400">{option.region_uk}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {cityRef ? (
        <div>
          {warehouseRef ? (
            <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
              <p className="text-xs font-medium text-brand-700">{T.selectedWarehouse}</p>
              <p className="mt-1 text-sm font-medium text-gray-900">{warehouse}</p>
              <button
                type="button"
                onClick={() => onWarehouseChange('', '')}
                className="mt-2 text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                {T.changeWarehouse}
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={warehouse}
                onChange={(e) => onWarehouseChange(e.target.value, '')}
                placeholder={T.warehousePlaceholder}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="mt-1 text-xs text-gray-400">
                {deferredWarehouse.length < 1
                  ? T.warehouseHint
                  : warehousesQuery.isLoading
                    ? T.loading
                    : warehouseOptions.length
                      ? T.warehouseHint
                      : T.noWarehouses}
              </p>
              {!warehouseRef && (
                <div className="mt-2 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white">
                  {warehousesQuery.isLoading && (
                    <p className="px-4 py-3 text-sm text-gray-500">{T.loading}</p>
                  )}
                  {!warehousesQuery.isLoading && warehouseOptions.length === 0 && (
                    <p className="px-4 py-3 text-sm text-gray-500">{T.noWarehouses}</p>
                  )}
                  {warehouseOptions.map((option) => (
                    <button
                      key={option.ref}
                      type="button"
                      onClick={() => onWarehouseChange(option.description_uk, option.ref)}
                      className="block w-full border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50"
                    >
                      <span className="block font-medium">{option.description_uk}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div>
          <input
            type="text"
            value={warehouse}
            onChange={(e) => onWarehouseChange(e.target.value, '')}
            placeholder={T.warehousePlaceholder}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="mt-1 text-xs text-gray-400">{T.warehouseHint}</p>
        </div>
      )}
    </div>
  )
}
